import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { FirPost86EpHistoryService } from './fir-post86-ep-history.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-post86-ep-history',
    templateUrl: './fir-post86-ep-history.component.html',
    styleUrls: ['./fir-post86-ep-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirPost86EpHistoryComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  title = 'Post-86 E&P History Report - Income/(Deficit)';
  minimise = false;
  loading = false;
  showData = false;
  displayTable = false;

  taxYearSL: Array<{ label: string; value: string }> = [];
  selectedTaxYear = '';
  tableColumns: string[] = [];
  tableRows: any[] = [];
  lineDescsCite: Record<string, string> = {};
  epTotalAmts: Record<string, any> = {};
  ptiTotalAmts: Record<string, Record<string, any>> = {};
  lineDescData: Record<string, Record<string, any>> = {};
  otherTaxYearsData: any[] = [];
  selectedHoLeidParams: any = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private service: FirPost86EpHistoryService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => { this.resetAndTryInit(); })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.resetAndTryInit(); })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) { fp = extractObject(hydrated); }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell, globalParams: mergedGp, filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.GENERAL_CONFIG?.base_url || !this.filterParams || Object.keys(this.filterParams).length === 0) return;
    this.isInitialized = true;
    this.refreshData();
  }

  refreshData(): void {
    if (this.isLoading) return;
    this.showData = true;
    this.loading = true;
    this.isLoading = true;
    this.displayTable = false;
    this.buildSelectedParams();
    this.loadHistoryData();
  }

  onTaxYearChange(value: string): void {
    if (!value) return;
    this.selectedTaxYear = value;
    this.loading = true;
    this.isLoading = true;
    this.displayTable = false;
    this.loadHistoryData(value);
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private buildSelectedParams(): void {
    this.selectedHoLeidParams = {
      ho_leid: this.filterParams?.ho_leid || this.filterParams?.fir_ho_leid || this.rowData?.HO_LEID,
      ho_cdr_no: this.filterParams?.ho_cdr_no || this.rowData?.HO_CDR_NO,
      ho_reporting_period: this.filterParams?.ho_reporting_period || this.rowData?.HO_REPORTING_PERIOD,
      filing_key: this.filterParams?.filing_key || this.globalService?.filing_key,
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.rowData?.JCD_KEY || this.filterParams?.jcd_key || this.globalService?.jcd_key || 250,
      me_string: this.filterParams?.me_string || '',
    };
  }

  private loadHistoryData(taxYear?: string): void {
    const params = { ...this.selectedHoLeidParams };
    if (taxYear) {
      params.tax_year = taxYear;
      const matchingYear = this.otherTaxYearsData.find((r: any) => String(r.TAX_YEAR) === String(taxYear));
      if (matchingYear) {
        params.scenario = matchingYear.SCENARIO;
        params.parent_me_string = matchingYear.ME_KEY;
      }
    }

    this.service.getPost86EpHistory(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        const json = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
        this.parseHistoryData(json);
        this.loading = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.tableColumns = [];
        this.tableRows = [];
        this.displayTable = false;
        this.loading = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private parseHistoryData(jsonObject: any[]): void {
    this.tableColumns = [];
    this.tableRows = [];
    this.taxYearSL = [];
    this.epTotalAmts = {};
    this.ptiTotalAmts = {};
    this.lineDescData = {};
    this.lineDescsCite = {};

    if (!jsonObject.length) {
      this.displayTable = false;
      return;
    }

    const first = jsonObject[0];
    const taxYearList = this.safeJson(first?.TAX_YEAR_LIST_JSON);
    taxYearList.forEach((r: any) => {
      if (r?.TAX_YEAR !== undefined && r?.TAX_YEAR !== null) {
        const value = String(r.TAX_YEAR);
        if (!this.taxYearSL.find((x) => x.value === value)) this.taxYearSL.push({ label: value, value });
      }
    });
    if (!this.selectedTaxYear && this.taxYearSL.length > 0) this.selectedTaxYear = this.taxYearSL[0].value;

    this.otherTaxYearsData = this.safeJson(first?.OTHER_TAX_YEARS_DATA_JSON);
    const epCategoryJson = this.safeJson(first?.GET_EP_CATEGORY_JSON);
    let prevPti = '';
    epCategoryJson.forEach((r: any) => {
      if (r?.PTI_TYPE && r.PTI_TYPE !== prevPti) this.tableColumns.push(`Total ${r.PTI_TYPE}`);
      if (r?.PTI_TYPE) prevPti = r.PTI_TYPE;
      if (r?.CATEGORY_NAME) this.tableColumns.push(r.CATEGORY_NAME);
    });

    const epTotalAmtsJson = this.safeJson(first?.GET_EP_TOTAL_AMTS_JSON);
    epTotalAmtsJson.forEach((r: any) => {
      if (r?.LINE_DESC !== undefined) this.epTotalAmts[String(r.LINE_DESC)] = r.TOTAL_AMT;
    });

    const ptiTotalAmtsJson = this.safeJson(first?.GET_PTI_TOTAL_AMTS_JSON);
    ptiTotalAmtsJson.forEach((r: any) => {
      const line = String(r?.LINE_DESC || '');
      if (!line) return;
      if (!this.ptiTotalAmts[line]) this.ptiTotalAmts[line] = {};
      this.ptiTotalAmts[line][`Total ${r.PTI_TYPE}`] = r.TOTAL_AMT;
    });

    const lineOrder: string[] = [];
    jsonObject.forEach((r: any) => {
      const line = String(r?.LINE_DESC || '');
      if (!line) return;
      if (!lineOrder.includes(line)) lineOrder.push(line);
      if (!this.lineDescData[line]) this.lineDescData[line] = {};
      if (!this.lineDescsCite[line]) this.lineDescsCite[line] = r?.CITE || '';
      if (r?.CATEGORY_NAME) this.lineDescData[line][r.CATEGORY_NAME] = r.AMT;
    });

    this.tableRows = lineOrder.map((line) => {
      const row: any = {
        Description: line,
        Cite: this.lineDescsCite[line] || '',
        'Total Amount': this.epTotalAmts[line] ?? '',
      };
      this.tableColumns.forEach((col) => {
        if (col.startsWith('Total ')) row[col] = this.ptiTotalAmts[line]?.[col] ?? '';
        else row[col] = this.lineDescData[line]?.[col] ?? '';
      });
      return row;
    });

    this.displayTable = this.tableRows.length > 0;
  }

  private safeJson(value: any): any[] {
    try {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      const parsed = safeJsonInput<any>(value, null, 'fir-post86-ep-history.parse1');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
